import { extractErrorDetail } from "@crm/errors";
import { Sentry } from "@crm/shared/sentry";
import { toast } from "sonner";

/**
 * 创建 mutation 的 onError 回调
 *
 * 自动从错误中提取消息并显示 toast 通知
 * 仅 5xx 服务器错误上报 Sentry（关联 serverRequestId）
 *
 * @param defaultMessage - 默认错误消息（当无法提取时使用）
 * @returns onError 回调函数
 *
 * @example 基础用法
 * ```tsx
 * useMutation({
 *   mutationFn: createStudentHandler,
 *   onError: useMutationError(),
 * })
 * ```
 *
 * @example 自定义消息
 * ```tsx
 * useMutation({
 *   mutationFn: deleteStudentHandler,
 *   onError: useMutationError("删除学员失败"),
 * })
 * ```
 */
export function reportMutationError(error: unknown, defaultMessage = "操作失败"): Promise<void> {
  return extractErrorDetail(error).then(({ message, requestId, statusCode }) => {
    if (requestId && statusCode && statusCode >= 500) {
      Sentry.withScope((scope: { setTag: (key: string, value: string) => void }) => {
        scope.setTag("serverRequestId", requestId);
        Sentry.captureMessage(`Server error: ${message}`);
      });
    }
    toast.error(message || defaultMessage);
  });
}

export function useMutationError(defaultMessage = "操作失败") {
  return (error: unknown) => reportMutationError(error, defaultMessage);
}
